import { BugAntIcon } from '@heroicons/react/24/outline';
import Logo from 'components/generic-components/Logo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CopyableText from 'components/generic-components/copyableText';
import { useCompany } from 'context/company';
import { useUser } from 'context/user';
import useOutsideClick from 'hooks/useOutsideClick';
import { useRouteAuthorization } from 'hooks/useRouteAuthorization';
import { CommonRoutes } from 'routes';
import { showIDToCopy } from 'utils/user';
import { MainNavItem } from './MainNavItem';
import MainNavRightSide from './MainNavRightSide';
import MobileGlassNav from './MobileGlassNav';
import { useNavigation } from './NavigationContext';
import { resolveActiveNav } from './navResolution';

const Navbar = ({ isFallback = false, containerClassName }: { isFallback?: boolean; containerClassName?: string }) => {
  const router = useRouter();
  const { currentCompany, roleType } = useUser() || {};
  const { company } = useCompany() || {};
  const { isAllowed } = useRouteAuthorization();
  const {
    navigation,
    activeSubNav: _activeSubNav,
    setActiveMainNav,
    setActiveSubNav,
    setActiveSubSideNav,
  } = useNavigation();

  const showCompanyIDToCopy = useMemo(() => {
    const companyToCheck = currentCompany || company;
    return showIDToCopy(companyToCheck);
  }, [currentCompany, company]);

  const {
    ref: debugPopupRef,
    isComponentVisible: showDebugPopup,
    setIsComponentVisible: setShowDebugPopup,
  } = useOutsideClick(false);

  // Collapse the full search bar into a compact icon button when the top nav
  // runs out of horizontal room (e.g. many apps enabled), so right-side items
  // like Settings and the profile menu don't get pushed off-screen.
  const navRowRef = useRef<HTMLDivElement>(null);
  const navLeftRef = useRef<HTMLDivElement>(null);
  const navRightRef = useRef<HTMLDivElement>(null);
  const [isSearchCollapsed, setIsSearchCollapsed] = useState(false);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;

    // Approx px width the search input reclaims when it collapses to an icon.
    // Used as hysteresis so the search doesn't flip-flop right at the threshold.
    const SEARCH_WIDTH_DELTA = 210;
    // Slack for inter-element margins not captured by scrollWidth.
    const BUFFER = 24;

    const measure = () => {
      const row = navRowRef.current;
      const left = navLeftRef.current;
      const right = navRightRef.current;
      if (!row || !left || !right) return;

      const available = row.clientWidth;
      const needed = left.scrollWidth + right.scrollWidth;

      setIsSearchCollapsed((collapsed) => {
        if (collapsed) {
          // `needed` already reflects the collapsed search; only expand back if
          // the full-width search would still fit.
          return needed + SEARCH_WIDTH_DELTA + BUFFER > available;
        }
        // Collapse as soon as the row can no longer fit its content.
        return needed + BUFFER > available;
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (navRowRef.current) observer.observe(navRowRef.current);
    if (navLeftRef.current) observer.observe(navLeftRef.current);
    if (navRightRef.current) observer.observe(navRightRef.current);
    return () => observer.disconnect();
  }, []);

  const handleFallbackRouting = useCallback(
    (href: string) => {
      if (!isFallback) return;
      window.location.replace(`${window.location.origin}${href}`);
    },
    [isFallback],
  );

  useEffect(() => {
    const { activeMainNav, activeSubNav, activeSubSideNav } = resolveActiveNav(
      navigation,
      router.pathname,
      router.asPath,
      isAllowed,
    );
    setActiveMainNav(activeMainNav);
    setActiveSubNav(activeSubNav);
    setActiveSubSideNav(activeSubSideNav);
  }, [navigation, router, setActiveMainNav, setActiveSubNav, setActiveSubSideNav, isAllowed]);

  return (
    <div
      id="mainNav-wrapper"
      className={`fixed left-0 right-0 top-0 border-b border-neutral-200 bg-white pt-[var(--safe-top)] md:mb-1 ${containerClassName}`}
    >
      <div className="px-4 md:px-0 md:pr-2">
        <div ref={navRowRef} className="flex h-16 items-center justify-between">
          {/* Left side with logo and menu */}
          <div ref={navLeftRef} className="flex flex-shrink-0 items-center">
            <Link
              href={CommonRoutes.Dashboard}
              onClick={() => handleFallbackRouting(CommonRoutes.Dashboard)}
              className="flex-shrink-0"
            >
              <Logo className="ml-0 h-8 w-auto px-0 md:ml-2 md:px-2" />
            </Link>

            {/* Desktop navigation */}
            <div className="hidden lg:ml-6 lg:flex lg:items-center lg:space-x-4">
              {roleType && (
                <ul className="flex items-center space-x-3">
                  {navigation?.map((item, index) => {
                    if (item.showInMainNav === false || !isAllowed(item?.onlyShownTo)) {
                      return null;
                    }
                    return <MainNavItem key={item.name} item={item} index={index} />;
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Right side with profile */}
          <div ref={navRightRef} className="lg:ml-2">
            <MainNavRightSide
              isFallback={isFallback}
              handleFallbackRouting={handleFallbackRouting}
              searchCollapsed={isSearchCollapsed}
            />
          </div>
        </div>

        {showCompanyIDToCopy && (
          <div className="fixed bottom-4 left-4 z-50 hidden lg:block" ref={debugPopupRef}>
            {showDebugPopup && (
              <div className="absolute bottom-11 left-0 rounded-md border border-gray-200 bg-white p-3 shadow-lg duration-150 animate-in fade-in-0 zoom-in-95">
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center space-x-1 text-xs">
                    Thera: <CopyableText text={company?.companyID || currentCompany?.companyID} trim />
                  </div>
                  {company?.w2Payroll?.checkId && (
                    <div className="flex items-center space-x-1 text-xs">
                      Check: <CopyableText text={company?.w2Payroll?.checkId} trim />
                    </div>
                  )}
                </div>
              </div>
            )}
            <button
              onClick={() => setShowDebugPopup(!showDebugPopup)}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-neutral-500 shadow-md hover:text-therablue"
              title="Debug Info"
            >
              <BugAntIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Mobile menu: floating liquid-glass tab bar */}
        <MobileGlassNav />
      </div>
    </div>
  );
};

const MemoizedNavbar = React.memo(Navbar);

export default MemoizedNavbar;
